import React, {
    FunctionComponent,
    KeyboardEvent,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState
} from "react";
import {AChartXProps} from "@atiautomacao/ati-ui-library";
import {useTheme} from "@mui/material";
import {format} from "date-fns";
import {getInstanceByDom, graphic, init} from "echarts";

export type TrackerPositionTime = string | number | Date;
export type TrackerPositionPoint = [TrackerPositionTime, unknown];

export interface TrackerPositionSeries {
    name?: string | null;
    data?: TrackerPositionPoint[];
    dataByTime?: TrackerPositionPoint[];
}

export interface TrackerPositionHeatmapPeriod {
    start: TrackerPositionTime;
    end: TrackerPositionTime;
}

export type TrackerPositionHeatmapVariant = "embedded" | "expanded";

export interface TrackerPositionHeatmapProps {
    contextId: string;
    series: TrackerPositionSeries[];
    period: TrackerPositionHeatmapPeriod;
    isMobile: boolean;
    variant?: TrackerPositionHeatmapVariant;
}

export interface TrackerHeatmapCell {
    timestamp: number;
    rowIndex: number;
    visualValue: number;
    rawValue: number;
    trackerName: string;
}

export interface TrackerHeatmapRow {
    rowIndex: number;
    trackerName: string;
    sourceIndex: number;
    cells: TrackerHeatmapCell[];
    hasValidMeasurements: boolean;
}

export interface TrackerHeatScale {
    rawMin: number;
    rawMax: number;
    visualMin: number;
    visualMax: number;
    isSingleValue: boolean;
}

const MAX_TRACKER_ROWS = 40;
const FIGMA_CHART_HEIGHT = 355;
const FIGMA_CELL_WIDTH = 11.38988208770752;
const FIGMA_CELL_HEIGHT = 12.099934577941895;
const CELL_GAP = 1;
const EXPANDED_CHART_RESERVED_HEIGHT = 42;
const EXPANDED_ROW_PITCH = 13;
const EXPANDED_INITIAL_HEIGHT = 562;
const FIVE_MINUTES = 5 * 60 * 1000;
const HEATMAP_COLORS = [
    "#fff185",
    "#f9d37a",
    "#f4b56f",
    "#ef9664",
    "#ea7859",
    "#e65b4f",
    "#e13b42",
    "#dd1e38",
    "#d7002c"
];

const parseMeasurement = (value: unknown): number | null => {
    if (value === null || value === undefined) {
        return null;
    }
    if (typeof value === "string" && (
        value.trim() === "" || value.trim().toLowerCase() === "null"
    )) {
        return null;
    }

    const numericValue = Number(value);
    return Number.isFinite(numericValue) ? numericValue : null;
};

const parseTimestamp = (value: TrackerPositionTime): number | null => {
    const timestamp = value instanceof Date ? value.getTime() : new Date(value).getTime();
    return Number.isFinite(timestamp) ? timestamp : null;
};

const clampPosition = (value: number) => Math.min(180, Math.max(-180, value));

const formatTime = (timestamp: number) => format(new Date(timestamp), "HH:mm");

const getColorForPosition = (value: number) => {
    const normalizedPosition = (clampPosition(value) + 180) / 360;
    const colorIndex = Math.round(normalizedPosition * (HEATMAP_COLORS.length - 1));
    return HEATMAP_COLORS[colorIndex];
};

export const buildTrackerHeatmapRows = (
    series: TrackerPositionSeries[],
    variant: TrackerPositionHeatmapVariant = "embedded"
): TrackerHeatmapRow[] => {
    const sortedSeries = series
        .map((trackerSeries, sourceIndex) => ({
            trackerSeries,
            sourceIndex,
            trackerName: String(trackerSeries.name ?? "")
        }))
        .sort((left, right) => {
            const naturalOrder = left.trackerName.localeCompare(
                right.trackerName,
                undefined,
                {numeric: true, sensitivity: "base"}
            );
            if (naturalOrder !== 0) {
                return naturalOrder;
            }

            const exactNameOrder = left.trackerName.localeCompare(
                right.trackerName,
                undefined,
                {numeric: true, sensitivity: "variant"}
            );
            return exactNameOrder !== 0
                ? exactNameOrder
                : left.sourceIndex - right.sourceIndex;
        });
    const visibleSeries = variant === "embedded"
        ? sortedSeries.slice(0, MAX_TRACKER_ROWS)
        : sortedSeries;

    return visibleSeries
        .map(({trackerSeries, sourceIndex, trackerName}, rowIndex) => {
            const points = trackerSeries.data ?? trackerSeries.dataByTime ?? [];
            const cells = points.reduce<TrackerHeatmapCell[]>((validCells, point) => {
                const timestamp = parseTimestamp(point?.[0]);
                const rawValue = parseMeasurement(point?.[1]);
                if (timestamp === null || rawValue === null) {
                    return validCells;
                }

                validCells.push({
                    timestamp,
                    rowIndex,
                    visualValue: clampPosition(rawValue),
                    rawValue,
                    trackerName
                });
                return validCells;
            }, []).sort((left, right) => left.timestamp - right.timestamp);

            return {
                rowIndex,
                trackerName,
                sourceIndex,
                cells,
                hasValidMeasurements: cells.length > 0
            };
        });
};

export const getExpandedVisibleCapacity = (availableHeight: number) => Math.max(
    1,
    Math.floor((Math.max(0, availableHeight) - EXPANDED_CHART_RESERVED_HEIGHT) /
        EXPANDED_ROW_PITCH)
);

export const resolveExpandedWindowStart = (
    event: unknown,
    rowCount: number,
    visibleCapacity: number
): number => {
    const maxWindowStart = Math.max(0, rowCount - Math.max(1, visibleCapacity));
    const payload = event as {
        start?: number;
        startValue?: number;
        batch?: Array<{start?: number; startValue?: number}>;
    };
    const zoom = payload?.batch?.[0] ?? payload ?? {};
    const percentageStart = Number(zoom.start);
    if (Number.isFinite(percentageStart)) {
        const dataExtent = Math.max(1, rowCount - 1);
        return Math.min(
            maxWindowStart,
            Math.max(0, Math.round((percentageStart / 100) * dataExtent))
        );
    }

    const explicitStart = Number(zoom.startValue);
    if (Number.isFinite(explicitStart)) {
        return Math.min(maxWindowStart, Math.max(0, Math.round(explicitStart)));
    }

    return 0;
};

interface TrackerHeatmapLayout {
    variant?: TrackerPositionHeatmapVariant;
    visibleCapacity?: number;
    windowStart?: number;
    fontFamily?: string;
}

export const getTrackerHeatScale = (
    rows: TrackerHeatmapRow[]
): TrackerHeatScale | null => {
    const values = rows.flatMap(row => row.cells.map(cell => cell.rawValue));
    if (values.length === 0) {
        return null;
    }

    const rawMin = Math.min(...values);
    const rawMax = Math.max(...values);
    const visualMin = clampPosition(rawMin);
    const visualMax = clampPosition(rawMax);

    return {
        rawMin,
        rawMax,
        visualMin,
        visualMax,
        isSingleValue: visualMin === visualMax
    };
};

export const buildTrackerHeatmapOption = (
    rows: TrackerHeatmapRow[],
    period: TrackerPositionHeatmapPeriod,
    isMobile: boolean,
    layout: TrackerHeatmapLayout = {}
): AChartXProps["option"] => {
    const variant = layout.variant ?? "embedded";
    const visibleCapacity = Math.max(
        1,
        layout.visibleCapacity ?? (variant === "expanded" ? rows.length : 24)
    );
    const maxWindowStart = Math.max(0, rows.length - visibleCapacity);
    const windowStart = Math.min(
        maxWindowStart,
        Math.max(0, layout.windowStart ?? 0)
    );
    const windowEnd = Math.min(
        rows.length - 1,
        windowStart + visibleCapacity - 1
    );
    const hasExpandedScroll = variant === "expanded" &&
        rows.length > visibleCapacity;
    const heatScale = getTrackerHeatScale(rows);
    const visualMin = heatScale?.visualMin ?? -180;
    const visualMax = heatScale?.visualMax ?? 180;
    const data = rows.flatMap(row => row.cells.map(cell => [
        cell.timestamp,
        cell.rowIndex,
        cell.visualValue,
        cell.rawValue
    ]));

    return {
        animation: false,
        aria: {
            enabled: true,
            description: "Mapa de calor das posições dos trackers ao longo do período."
        },
        grid: {
            top: 8.86083984375,
            right: hasExpandedScroll ? 48 : 28,
            bottom: 32.74072265625,
            left: 146.646484375,
            containLabel: false
        },
        xAxis: {
            type: "time",
            min: parseTimestamp(period.start),
            max: parseTimestamp(period.end),
            minInterval: isMobile ? 10800000 : 3600000,
            maxInterval: isMobile ? 10800000 : 3600000,
            axisLabel: {
                color: "rgba(0,0,0,0.5)",
                fontFamily: layout.fontFamily,
                fontSize: 12,
                letterSpacing: 0.6,
                formatter: (value: number) => formatTime(value)
            },
            axisLine: {show: false},
            axisTick: {show: false},
            splitLine: {show: false}
        },
        yAxis: {
            type: "category",
            inverse: true,
            data: rows.map(row => row.trackerName),
            axisLabel: {
                color: "rgba(0,0,0,0.5)",
                fontFamily: layout.fontFamily,
                fontSize: 10,
                margin: 90,
                width: 63,
                overflow: "truncate",
                align: "center"
            },
            axisLine: {show: false},
            axisTick: {show: false},
            splitLine: {show: false}
        },
        dataZoom: variant === "expanded"
            ? hasExpandedScroll ? [
                {
                    id: "tracker-heatmap-inside",
                    type: "inside",
                    yAxisIndex: 0,
                    startValue: windowStart,
                    endValue: windowEnd,
                    filterMode: "weakFilter",
                    zoomLock: true,
                    moveOnMouseWheel: true,
                    moveOnMouseMove: true,
                    preventDefaultMouseMove: true
                },
                {
                    id: "tracker-heatmap-slider",
                    type: "slider",
                    yAxisIndex: 0,
                    orient: "vertical",
                    right: 4,
                    top: 8.86083984375,
                    bottom: 32.74072265625,
                    width: 12,
                    startValue: windowStart,
                    endValue: windowEnd,
                    filterMode: "weakFilter",
                    zoomLock: true,
                    realtime: true,
                    brushSelect: false,
                    showDataShadow: false,
                    showDetail: false
                }
            ] : []
            : rows.length > 24 ? [{
                type: "inside",
                yAxisIndex: 0,
                startValue: 0,
                endValue: 23,
                zoomLock: true,
                moveOnMouseWheel: true,
                moveOnMouseMove: true,
                preventDefaultMouseMove: true
            }] : [],
        visualMap: {
            show: false,
            min: visualMin,
            max: visualMax,
            dimension: 2,
            inRange: {
                color: heatScale?.isSingleValue
                    ? [getColorForPosition(visualMin)]
                    : HEATMAP_COLORS
            }
        },
        series: [{
            name: "Posição dos trackers",
            type: "custom",
            coordinateSystem: "cartesian2d",
            dimensions: ["timestamp", "tracker", "visualValue", "rawValue"],
            encode: {
                x: 0,
                y: 1,
                tooltip: [0, 3]
            },
            data,
            renderItem: (params: any, api: any) => {
                const position = api.coord([api.value(0), api.value(1)]);
                const rowHeight = Math.min(
                    FIGMA_CELL_HEIGHT,
                    Math.max(2, Math.abs(api.size([0, 1])[1]) - CELL_GAP)
                );
                const cellWidth = Math.min(
                    FIGMA_CELL_WIDTH,
                    Math.max(2, Math.abs(api.size([FIVE_MINUTES, 0])[0]) - CELL_GAP)
                );
                const shape = graphic.clipRectByRect({
                    x: position[0] - cellWidth / 2,
                    y: position[1] - rowHeight / 2,
                    width: cellWidth,
                    height: rowHeight
                }, params.coordSys);

                return shape ? {
                    type: "rect",
                    shape,
                    style: api.style()
                } : null;
            }
        }],
        tooltip: {
            trigger: "item",
            renderMode: "richText",
            appendToBody: true,
            confine: true,
            formatter: (params: any) => {
                const value = params?.value ?? [];
                const row = rows[Number(value[1])];
                const timestamp = Number(value[0]);
                const rawValue = Number(value[3]);
                if (!row || !Number.isFinite(timestamp) || !Number.isFinite(rawValue)) {
                    return "";
                }
                return `${row.trackerName} • ${formatTime(timestamp)} • ${rawValue}°`;
            }
        },
        toolbox: {
            feature: {
                saveAsImage: {show: false}
            }
        }
    } as unknown as AChartXProps["option"];
};

interface ActiveHeatmapCell {
    rowIndex: number;
    cellIndex: number;
}

const describeActiveCell = (
    rows: TrackerHeatmapRow[],
    activeCell: ActiveHeatmapCell,
    separator: ", " | " • "
) => {
    const row = rows[activeCell.rowIndex];
    if (!row) {
        return "Nenhum tracker disponível no período";
    }
    if (row.cells.length === 0) {
        return `${row.trackerName}${separator}sem medições no período`;
    }

    const cell = row.cells[Math.min(activeCell.cellIndex, row.cells.length - 1)];
    return [
        row.trackerName,
        formatTime(cell.timestamp),
        `${cell.rawValue}°`
    ].join(separator);
};

const TrackerPositionHeatmap: FunctionComponent<TrackerPositionHeatmapProps> = ({
    contextId,
    series,
    period,
    isMobile,
    variant = "embedded"
}) => {
    const theme = useTheme();
    const rows = useMemo(
        () => buildTrackerHeatmapRows(series, variant),
        [series, variant]
    );
    const [availableHeight, setAvailableHeight] = useState(
        variant === "expanded" ? EXPANDED_INITIAL_HEIGHT : FIGMA_CHART_HEIGHT
    );
    const physicalCapacity = variant === "expanded"
        ? getExpandedVisibleCapacity(availableHeight)
        : Math.min(MAX_TRACKER_ROWS, Math.max(1, rows.length));
    const visibleCapacity = Math.min(rows.length, physicalCapacity);
    const [windowStart, setWindowStart] = useState(0);
    const [windowRevision, setWindowRevision] = useState(0);
    const maxWindowStart = Math.max(0, rows.length - Math.max(1, visibleCapacity));
    const safeWindowStart = Math.min(maxWindowStart, windowStart);
    const windowEnd = rows.length === 0
        ? -1
        : Math.min(
            rows.length - 1,
            safeWindowStart + Math.max(1, visibleCapacity) - 1
        );
    const hasVerticalScroll = variant === "expanded" &&
        rows.length > physicalCapacity;
    const heatScale = useMemo(() => getTrackerHeatScale(rows), [rows]);
    const option = useMemo(
        () => buildTrackerHeatmapOption(rows, period, isMobile, {
            variant,
            visibleCapacity: Math.max(1, visibleCapacity),
            windowStart: 0,
            fontFamily: theme.typography.fontFamily
        }),
        [isMobile, period, rows, theme.typography.fontFamily, variant, visibleCapacity]
    );
    const chartWrapperRef = useRef<HTMLDivElement>(null);
    const chartElementRef = useRef<HTMLDivElement>(null);
    const currentContextRef = useRef(contextId);
    const zoomSyncSourceRef = useRef<"programmatic" | "chart">("programmatic");
    const [renderState, setRenderState] = useState<"pending" | "finished">("pending");
    const [activeCell, setActiveCell] = useState<ActiveHeatmapCell>({
        rowIndex: 0,
        cellIndex: 0
    });
    const [pinnedDetail, setPinnedDetail] = useState<string | null>(null);

    currentContextRef.current = contextId;
    const safeWindowStartRef = useRef(safeWindowStart);
    safeWindowStartRef.current = safeWindowStart;

    useEffect(() => {
        setActiveCell({rowIndex: 0, cellIndex: 0});
        setPinnedDetail(null);
        zoomSyncSourceRef.current = "programmatic";
        setWindowStart(0);
        setWindowRevision(0);
    }, [contextId, rows]);

    useEffect(() => {
        zoomSyncSourceRef.current = "programmatic";
        setWindowStart(current => Math.min(maxWindowStart, current));
    }, [maxWindowStart]);

    useEffect(() => {
        const chartElement = chartElementRef.current;
        if (!chartElement) {
            return;
        }

        const chart = init(chartElement);
        return () => {
            chart.dispose();
        };
    }, []);

    useEffect(() => {
        setRenderState("pending");
        const capturedContextId = contextId;
        const chartElement = chartElementRef.current;
        const chart = chartElement ? getInstanceByDom(chartElement) : undefined;
        if (!chart) {
            return;
        }

        const handleFinished = () => {
            if (currentContextRef.current === capturedContextId) {
                setRenderState("finished");
            }
        };
        chart.on("finished", handleFinished);
        chart.setOption(option, false, false);

        return () => {
            chart.off("finished", handleFinished);
        };
    }, [contextId, option]);

    useEffect(() => {
        if (variant !== "expanded") {
            return;
        }

        const chartElement = chartElementRef.current;
        const chart = chartElement ? getInstanceByDom(chartElement) : undefined;
        if (!chart) {
            return;
        }

        const handleDataZoom = (event: unknown) => {
            const clampedStart = resolveExpandedWindowStart(
                event,
                rows.length,
                Math.max(1, visibleCapacity)
            );

            zoomSyncSourceRef.current = "chart";
            setWindowStart(current => {
                if (current === clampedStart) {
                    return current;
                }
                setWindowRevision(revision => revision + 1);
                return clampedStart;
            });
        };

        chart.on("datazoom", handleDataZoom);
        return () => {
            chart.off("datazoom", handleDataZoom);
        };
    }, [rows.length, variant, visibleCapacity]);

    const applyExpandedWindow = useCallback((start: number) => {
        const chartElement = chartElementRef.current;
        const chart = chartElement ? getInstanceByDom(chartElement) : undefined;
        if (!chart) {
            return;
        }

        const endValue = Math.min(
            rows.length - 1,
            start + Math.max(1, visibleCapacity) - 1
        );
        chart.dispatchAction({
            type: "dataZoom",
            batch: [
                {
                    dataZoomId: "tracker-heatmap-inside",
                    startValue: start,
                    endValue
                },
                {
                    dataZoomId: "tracker-heatmap-slider",
                    startValue: start,
                    endValue
                }
            ]
        });
    }, [rows.length, visibleCapacity]);

    useEffect(() => {
        if (variant !== "expanded" || !hasVerticalScroll) {
            return;
        }

        applyExpandedWindow(safeWindowStartRef.current);
    }, [
        applyExpandedWindow,
        hasVerticalScroll,
        rows.length,
        variant,
        visibleCapacity
    ]);

    useEffect(() => {
        if (variant !== "expanded" || !hasVerticalScroll) {
            return;
        }
        if (zoomSyncSourceRef.current === "chart") {
            return;
        }

        applyExpandedWindow(safeWindowStart);
    }, [
        applyExpandedWindow,
        hasVerticalScroll,
        safeWindowStart,
        variant
    ]);

    useEffect(() => {
        const wrapper = chartWrapperRef.current;
        const chartElement = chartElementRef.current;
        if (!wrapper || !chartElement || typeof ResizeObserver === "undefined") {
            return;
        }

        const initialRect = wrapper.getBoundingClientRect();
        let lastWidth = initialRect.width;
        let lastHeight = initialRect.height;
        if (variant === "expanded" && initialRect.height > 0) {
            setAvailableHeight(initialRect.height);
        }
        const resizeObserver = new ResizeObserver(entries => {
            const currentRect = entries[0]?.contentRect;
            if (!currentRect ||
                currentRect.width <= 0 ||
                currentRect.height <= 0 ||
                (currentRect.width === lastWidth && currentRect.height === lastHeight)) {
                return;
            }

            lastWidth = currentRect.width;
            lastHeight = currentRect.height;
            if (variant === "expanded" && currentRect.height > 0) {
                setAvailableHeight(currentRect.height);
            }
            getInstanceByDom(chartElement)?.resize();
        });
        resizeObserver.observe(wrapper);

        return () => {
            resizeObserver.disconnect();
        };
    }, [variant]);

    const keepRowVisible = useCallback((rowIndex: number) => {
        if (variant !== "expanded" || !hasVerticalScroll) {
            return;
        }

        setWindowStart(current => {
            let next = current;
            if (rowIndex < current) {
                next = rowIndex;
            } else if (rowIndex >= current + Math.max(1, visibleCapacity)) {
                next = rowIndex - Math.max(1, visibleCapacity) + 1;
            }
            next = Math.min(maxWindowStart, Math.max(0, next));
            if (next !== current) {
                zoomSyncSourceRef.current = "programmatic";
                setWindowRevision(revision => revision + 1);
            }
            return next;
        });
    }, [hasVerticalScroll, maxWindowStart, variant, visibleCapacity]);

    const handleKeyboardNavigation = (event: KeyboardEvent<HTMLDivElement>) => {
        const navigationKeys = [
            "ArrowUp",
            "ArrowDown",
            "ArrowLeft",
            "ArrowRight",
            "Home",
            "End"
        ];

        if (navigationKeys.includes(event.key)) {
            event.preventDefault();
            setActiveCell(current => {
                let nextRowIndex = current.rowIndex;
                let nextCellIndex = current.cellIndex;

                if (event.key === "ArrowUp") {
                    nextRowIndex = Math.max(0, current.rowIndex - 1);
                } else if (event.key === "ArrowDown") {
                    nextRowIndex = Math.min(rows.length - 1, current.rowIndex + 1);
                } else if (event.key === "ArrowLeft") {
                    nextCellIndex = Math.max(0, current.cellIndex - 1);
                } else if (event.key === "ArrowRight") {
                    nextCellIndex = Math.min(
                        Math.max(0, (rows[current.rowIndex]?.cells.length ?? 1) - 1),
                        current.cellIndex + 1
                    );
                } else if (event.key === "Home") {
                    nextCellIndex = 0;
                } else if (event.key === "End") {
                    nextCellIndex = Math.max(
                        0,
                        (rows[current.rowIndex]?.cells.length ?? 1) - 1
                    );
                }

                const nextRowCellCount = rows[nextRowIndex]?.cells.length ?? 0;
                nextCellIndex = nextRowCellCount === 0
                    ? 0
                    : Math.min(nextCellIndex, nextRowCellCount - 1);
                keepRowVisible(nextRowIndex);
                return {rowIndex: nextRowIndex, cellIndex: nextCellIndex};
            });
            return;
        }

        if (event.key === "Enter" || event.key === " " || event.key === "Spacebar") {
            event.preventDefault();
            setPinnedDetail(describeActiveCell(rows, activeCell, " • "));
        } else if (event.key === "Escape") {
            if (variant === "embedded") {
                event.preventDefault();
            }
            setPinnedDetail(null);
        }
    };

    const visualMin = heatScale?.visualMin ?? -180;
    const visualMax = heatScale?.visualMax ?? 180;
    const scaleAriaLabel = heatScale
        ? heatScale.isSingleValue
            ? `Escala de calor: ${visualMin} graus`
            : `Escala de calor: mínimo ${visualMin} graus e máximo ${visualMax} graus`
        : "Escala de calor: sem medições válidas";

    return (
        <div
            className={`tracker-position-heatmap tracker-position-heatmap--${variant}`}
            data-render-context={contextId}
            data-render-state={renderState}
            data-period-start={parseTimestamp(period.start) ?? ""}
            data-period-end={parseTimestamp(period.end) ?? ""}
            data-total-row-count={rows.length}
            data-visible-capacity={visibleCapacity}
            data-window-start={safeWindowStart}
            data-window-end={windowEnd}
            data-window-revision={windowRevision}
            data-has-vertical-scroll={hasVerticalScroll}
        >
            <div
                className={`tracker-position-heatmap-scale${
                    heatScale?.isSingleValue ? " single-value" : ""
                }`}
                aria-label={scaleAriaLabel}
            >
                {heatScale?.isSingleValue ? (
                    <>
                        <i
                            className="tracker-position-heatmap-scale-marker single"
                            style={{backgroundColor: getColorForPosition(visualMin)}}
                        />
                        <span
                            className="tracker-position-heatmap-gradient"
                            style={{background: getColorForPosition(visualMin)}}
                        />
                        <span>{visualMin}°</span>
                    </>
                ) : (
                    <>
                        <span>{visualMin}°</span>
                        <i className="tracker-position-heatmap-scale-marker start"/>
                        <span className="tracker-position-heatmap-gradient"/>
                        <i className="tracker-position-heatmap-scale-marker end"/>
                        <span>{visualMax}°</span>
                    </>
                )}
            </div>
            <div
                className="tracker-position-heatmap-scroll"
                data-testid="tracker-heatmap-scroll"
                data-visible-row-limit={
                    variant === "embedded" ? MAX_TRACKER_ROWS : visibleCapacity
                }
            >
                <div
                    ref={chartWrapperRef}
                    className="tracker-position-heatmap-chart"
                    role="group"
                    aria-label="Mapa de calor das posições dos trackers"
                    tabIndex={0}
                    onKeyDown={handleKeyboardNavigation}
                >
                    <div
                        ref={chartElementRef}
                        data-testid="tracker-heatmap-chart"
                        data-height={
                            variant === "embedded" ? FIGMA_CHART_HEIGHT : availableHeight
                        }
                        data-row-count={rows.length}
                        style={{
                            height: variant === "embedded" ? FIGMA_CHART_HEIGHT : "100%",
                            width: "100%"
                        }}
                    />
                </div>
            </div>
            <div
                className="tracker-position-heatmap-live"
                role="status"
                aria-live="polite"
                aria-atomic="true"
            >
                {describeActiveCell(rows, activeCell, ", ")}
            </div>
            {pinnedDetail && (
                <div className="tracker-position-heatmap-pinned-detail" role="note">
                    {pinnedDetail}
                </div>
            )}
            <ul
                className="tracker-position-heatmap-semantic"
                aria-label="Resumo das posições por tracker"
            >
                {rows.map(row => (
                    <li key={`${row.sourceIndex}-${row.trackerName}`}>
                        {row.trackerName}: {row.hasValidMeasurements
                            ? `${row.cells.length} ${row.cells.length === 1 ? "medição" : "medições"} no período`
                            : "sem medições no período"}
                    </li>
                ))}
            </ul>
        </div>
    );
};

export default TrackerPositionHeatmap;
